import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import OpenCashBoxForm from '../components/OpenCashBoxForm';
import CashBoxOverview from '../components/CashBoxOverview';
import ExpenseModal from '../components/ExpenseModal';
import RouteReceptionTab from '../components/RouteReceptionTab';
import ExpensesTab from '../components/ExpensesTab';
import AuditTab from '../components/AuditTab';
import AlertsTab from '../components/AlertsTab';
import api from '../services/api';
import './CashBoxDashboard.css';

/**
 * Cash Box Dashboard - Main container for petty cash management
 * Handles opening, closing, expenses, and balance tracking
 */
const CashBoxDashboard = () => {
    // Helper to get Colombia date (YYYY-MM-DD) correctly
    const getColombiaDate = () => {
        return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
    };

    const [currentSession, setCurrentSession] = useState(null);
    const [lastSessionBaseLeft, setLastSessionBaseLeft] = useState(0);
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [selectedBox, setSelectedBox] = useState('Caja Principal');

    const [selectedDate, setSelectedDate] = useState(getColombiaDate());

    useEffect(() => {
        const today = getColombiaDate();
        if (selectedDate === today) {
            fetchCurrentSession();
        } else {
            fetchHistoricalSession(selectedDate);
        }
    }, [selectedBox, selectedDate]);



    useEffect(() => {
        if (currentSession) {
            // Only fetch LIVE balance if we are looking at TODAY's session
            // Historical sessions have static balance loaded from the session record itself
            const today = getColombiaDate();
            if (selectedDate === today) {
                fetchBalance();
            }
        }
    }, [currentSession, selectedDate]);

    // WebSocket for Real-time Updates
    useEffect(() => {
        // Use same connection logic as NotificationSystem or default to origin
        const socketUrl = process.env.REACT_APP_SOCKET_URL || window.location.origin.replace(/^http/, 'ws');

        // Note: In some setups, simple io() or io('/') works best if served from same origin
        const socket = io(socketUrl, {
            path: '/socket.io',
            transports: ['websocket', 'polling'],
            withCredentials: true
        });

        socket.on('connect', () => {
            console.log('🔌 CashBoxDashboard connected to WebSocket');
        });

        socket.on('cash_box_update', (data) => {
            console.log('💰 Cash Box Update received:', data);
            // Refresh data if the update is relevant to the selected box
            // AND only if we are viewing TODAY (Live View)
            const today = getColombiaDate();
            if ((!data.box_name || data.box_name === selectedBox) && selectedDate === today) {
                fetchCurrentSession();
                fetchBalance();
            }
        });

        return () => {
            socket.disconnect();
        };
    }, [selectedBox, selectedDate]);

    const fetchCurrentSession = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/cash-box/current?box_name=${encodeURIComponent(selectedBox)}`);
            if (response.data.session) {
                // Fetch full details to get calculated totals if needed
                if (response.data.session.status === 'open') {
                    // For open session, we might want current live balance which getCashBalance provides
                    // But let's stick to session object for now as it has ID
                    setCurrentSession(response.data.session);
                } else {
                    // If somehow we get a closed session as current (unlikely for /current endpoint logic), verify
                    setCurrentSession(response.data.session);
                }
                setLastSessionBaseLeft(0);
            } else {
                setCurrentSession(null);
                setLastSessionBaseLeft(response.data.last_session_base_left || 0);
            }
        } catch (error) {
            console.error('Error fetching current session:', error);
            setCurrentSession(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistoricalSession = async (date) => {
        try {
            setLoading(true);
            // 1. Find session for this date
            const listResponse = await api.get(`/cash-box/sessions`, {
                params: {
                    box_name: selectedBox,
                    from_date: date,
                    to_date: date,
                    limit: 1
                }
            });

            if (listResponse.data.sessions && listResponse.data.sessions.length > 0) {
                const session = listResponse.data.sessions[0];
                // 2. Get full details (including calculated totals)
                const detailsResponse = await api.get(`/cash-box/sessions/${session.id}`);

                // Merge details into session object for display
                const fullSession = {
                    ...detailsResponse.data.session,
                    ...detailsResponse.data.result // If result exists (e.g. closing snapshot)
                };

                setCurrentSession(fullSession);

                // For historical sessions, balance is static from the session record
                // We construct a balance object compatible with CashBoxOverview
                setBalance({
                    opening_balance: fullSession.opening_balance,
                    total_income: fullSession.calculated_total_income || 0, // Fallback if not mapped
                    total_expenses: fullSession.calculated_total_expenses || 0,
                    expected_balance: fullSession.calculated_expected_balance || 0,
                    actual_balance: fullSession.closing_balance_declared
                });

            } else {
                setCurrentSession(null);
                setBalance(null);
            }
        } catch (error) {
            console.error('Error fetching historical session:', error);
            setCurrentSession(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchBalance = async () => {
        try {
            const response = await api.get(`/cash-box/balance?box_name=${encodeURIComponent(selectedBox)}`);
            setBalance(response.data);
        } catch (error) {
            console.error('Error fetching balance:', error);
        }
    };

    const handleSessionOpened = () => {
        fetchCurrentSession();
        fetchBalance();
    };

    const handleSessionClosed = async (closingResult) => {
        // Reload the just-closed session so the closing report renders
        const today = getColombiaDate();
        if (selectedDate === today) {
            await fetchHistoricalSession(today);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-lg">Cargando...</div>
            </div>
        );
    }

    return (
        <div className="cash-box-dashboard p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800">💰 Gestión de Caja</h1>
                <p className="text-gray-600 mt-1">Sistema de control de efectivo y egresos</p>
            </div>

            {/* Box Selector & Date Picker */}
            <div className="flex flex-col md:flex-row gap-4 mb-4 items-end">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Caja activa:</label>
                    <select
                        value={selectedBox}
                        onChange={(e) => setSelectedBox(e.target.value)}
                        className="border border-gray-300 rounded px-3 py-2 w-full md:w-auto"
                    >
                        <option value="Caja Principal">Caja Principal</option>
                        {/* Future: Load from cash_boxes table */}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha:</label>
                    <input
                        type="date"
                        value={selectedDate}
                        max={getColombiaDate()}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="border border-gray-300 rounded px-3 py-2 w-full md:w-auto"
                    />
                </div>

                {selectedDate !== getColombiaDate() && (
                    <div className="pb-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            🗓️ Vista Histórica
                        </span>
                    </div>
                )}
            </div>

            {!currentSession ? (
                /* No session found */
                <div>
                    {selectedDate === getColombiaDate() ? (
                        /* Today: Show opening form */
                        <OpenCashBoxForm
                            boxName={selectedBox}
                            lastBaseLeft={lastSessionBaseLeft}
                            onSuccess={() => fetchCurrentSession()}
                        />
                    ) : (
                        /* Past Date: Show "No Data" message */
                        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="text-4xl mb-4">📭</div>
                            <h3 className="text-lg font-medium text-gray-900">No hay caja registrada</h3>
                            <p className="text-gray-500">No se encontró una sesión de caja para el {selectedDate}.</p>
                        </div>
                    )}
                    {/* Always show session history so users can browse previous sessions */}
                    <div className="mt-6">
                        <AuditTab />
                    </div>
                </div>
            ) : (
                /* Active session - Show tabs */
                <Tabs defaultValue="overview" className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="overview">💰 Caja</TabsTrigger>
                        <TabsTrigger value="income">📥 Ingresos</TabsTrigger>
                        <TabsTrigger value="expenses">📤 Egresos</TabsTrigger>
                        <TabsTrigger value="audit">📊 Auditoría</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview">
                        <CashBoxOverview
                            session={currentSession}
                            balance={balance}
                            onRefresh={fetchBalance}
                            onSessionClosed={handleSessionClosed}
                            onOpenExpense={() => setShowExpenseModal(true)}
                        />
                    </TabsContent>

                    <TabsContent value="income">
                        <RouteReceptionTab session={currentSession} balance={balance} />
                    </TabsContent>

                    <TabsContent value="expenses">
                        <ExpensesTab session={currentSession} />
                    </TabsContent>

                    <TabsContent value="audit">
                        <AuditTab />
                    </TabsContent>

                    <TabsContent value="alerts">
                        <AlertsTab />
                    </TabsContent>
                </Tabs>
            )}

            {/* Expense Modal */}
            {showExpenseModal && (
                <ExpenseModal
                    sessionId={currentSession?.id}
                    onClose={() => setShowExpenseModal(false)}
                    onSuccess={() => {
                        setShowExpenseModal(false);
                        fetchBalance();
                    }}
                />
            )}
        </div>
    );
};

export default CashBoxDashboard;
